/**
 * Reducers - Pure State Update Functions
 * 
 * This module implements Redux-style reducers that handle state updates
 * in a predictable, immutable way. Each reducer is a pure function that
 * takes the current state and an action, and returns a new state.
 * 
 * @module store/reducers
 */

import { ActionTypes } from './actions.js';
import Config from '../core/Config.js';

/**
 * UI State Reducer
 * Handles all UI-related state changes
 */
export const uiReducer = (state = {}, action) => {
  switch (action.type) {
    case ActionTypes.SET_CURRENT_TAB:
      return {
        ...state,
        currentTab: action.payload
      };

    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: action.payload.isLoading,
        loadingMessage: action.payload.message
      };

    case ActionTypes.SHOW_MODAL:
      return {
        ...state,
        modalOpen: action.payload.modalName,
        modalProps: action.payload.props
      };

    case ActionTypes.HIDE_MODAL:
      return {
        ...state,
        modalOpen: null,
        modalProps: {}
      };

    case ActionTypes.TOGGLE_SIDEBAR:
      return {
        ...state,
        sidebarVisible: !state.sidebarVisible
      };

    case ActionTypes.SET_THEME:
      return {
        ...state,
        theme: action.payload
      };

    case ActionTypes.SHOW_TOAST:
      return {
        ...state,
        toasts: [
          ...(state.toasts || []),
          action.payload
        ]
      };

    case ActionTypes.HIDE_TOAST:
      return {
        ...state,
        toasts: (state.toasts || []).filter(toast => toast.id !== action.payload)
      };

    default:
      return state;
  }
};

/**
 * Initiatives Reducer
 * Handles initiative management state
 */
export const initiativesReducer = (state = [], action) => {
  switch (action.type) {
    case ActionTypes.SET_INITIATIVES:
      return Array.isArray(action.payload) ? action.payload : [];

    case ActionTypes.ADD_INITIATIVE:
      // Ensure we don't add duplicates
      const existingIndex = state.findIndex(i => i.id === action.payload.id);
      if (existingIndex >= 0) {
        console.warn(`Initiative with ID ${action.payload.id} already exists`);
        return state;
      }
      
      return [
        ...state,
        {
          ...action.payload,
          createdAt: action.payload.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }
      ];

    case ActionTypes.UPDATE_INITIATIVE:
      return state.map(initiative => {
        if (initiative.id === action.payload.id) {
          return {
            ...initiative,
            ...action.payload.updates,
            updatedAt: new Date().toISOString()
          };
        }
        return initiative;
      });

    case ActionTypes.DELETE_INITIATIVE:
      return state.filter(initiative => initiative.id !== action.payload);

    case ActionTypes.SELECT_INITIATIVE:
      // This doesn't change the initiatives array, just marks selection
      // Selection state is handled separately
      return state;

    default:
      return state;
  }
};

/**
 * Chat State Reducer
 * Handles chat messages and conversation state
 */
export const chatReducer = (state = { messages: [], typing: false, conversationDepth: 0, context: null }, action) => {
  switch (action.type) {
    case ActionTypes.ADD_CHAT_MESSAGE:
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            ...action.payload,
            timestamp: action.payload.timestamp || new Date().toISOString()
          }
        ],
        conversationDepth: state.conversationDepth + 1
      };

    case ActionTypes.CLEAR_CHAT:
      return {
        ...state,
        messages: [],
        conversationDepth: 0,
        context: null,
        typing: false
      };

    case ActionTypes.SET_TYPING:
      return {
        ...state,
        typing: action.payload
      };

    case ActionTypes.UPDATE_CONVERSATION_CONTEXT:
      return {
        ...state,
        context: {
          ...state.context,
          ...action.payload
        }
      };

    case ActionTypes.SET_CONVERSATION_DEPTH:
      return {
        ...state,
        conversationDepth: action.payload
      };

    default:
      return state;
  }
};

/**
 * User Settings Reducer
 * Handles user preferences and configuration
 */
export const userReducer = (state = {}, action) => {
  switch (action.type) {
    case ActionTypes.SET_API_KEY:
      return {
        ...state,
        apiKey: action.payload
      };

    case ActionTypes.SET_MODEL:
      return {
        ...state,
        model: action.payload
      };

    case ActionTypes.UPDATE_SETTINGS:
      return {
        ...state,
        ...action.payload
      };

    case ActionTypes.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload
        }
      };

    default:
      return state;
  }
};

/**
 * Strategy Reducer
 * Handles strategy documents and analysis
 */
export const strategiesReducer = (state = { uploaded: null, emergent: null, current: null }, action) => {
  switch (action.type) {
    case ActionTypes.SET_UPLOADED_STRATEGY:
      return {
        ...state,
        uploaded: action.payload,
        current: action.payload // Auto-set as current when uploaded
      };

    case ActionTypes.SET_EMERGENT_STRATEGY:
      return {
        ...state,
        emergent: action.payload,
        current: state.current || action.payload // Set as current if no current strategy
      };

    case ActionTypes.SET_CURRENT_STRATEGY:
      return {
        ...state,
        current: action.payload
      };

    case ActionTypes.CLEAR_STRATEGIES:
      return {
        uploaded: null,
        emergent: null,
        current: null
      };

    default:
      return state;
  }
};

/**
 * Portfolio Reducer
 * Handles portfolio analysis and conflicts
 */
export const portfolioReducer = (state = { analysis: null, conflicts: [], lastAnalyzed: null }, action) => {
  switch (action.type) {
    case ActionTypes.SET_PORTFOLIO_ANALYSIS:
      return {
        ...state,
        analysis: action.payload,
        lastAnalyzed: action.payload.analyzedAt || new Date().toISOString()
      };

    case ActionTypes.SET_PORTFOLIO_CONFLICTS:
      return {
        ...state,
        conflicts: Array.isArray(action.payload) ? action.payload : []
      };

    case ActionTypes.UPDATE_PORTFOLIO_STATUS:
      return {
        ...state,
        status: action.payload
      };

    default:
      return state;
  }
};

/**
 * System Reducer
 * Handles system-level state like errors, version, initialization
 */
export const systemReducer = (state = { version: Config.APP_VERSION, initialized: false, errors: [] }, action) => {
  switch (action.type) {
    case ActionTypes.APP_INITIALIZED:
      return {
        ...state,
        initialized: true,
        initializeAt: new Date().toISOString()
      };

    case ActionTypes.APP_ERROR:
      return {
        ...state,
        errors: [
          ...state.errors,
          {
            ...action.payload,
            id: Date.now(),
            timestamp: action.payload.timestamp || new Date().toISOString()
          }
        ]
      };

    case ActionTypes.ADD_ERROR:
      return {
        ...state,
        errors: [
          ...state.errors,
          {
            ...action.payload,
            id: Date.now(),
            timestamp: new Date().toISOString()
          }
        ]
      };

    case ActionTypes.CLEAR_ERRORS:
      return {
        ...state,
        errors: []
      };

    case ActionTypes.SET_VERSION:
      return {
        ...state,
        version: action.payload
      };

    case ActionTypes.SET_INITIALIZED:
      return {
        ...state,
        initialized: action.payload
      };

    case ActionTypes.APP_RESET:
      return {
        version: state.version, // Preserve version
        initialized: false,
        errors: [],
        resetAt: new Date().toISOString(),
        resetOptions: action.payload
      };

    default:
      return state;
  }
};

/**
 * Selection State Reducer
 * Handles selection state across different domains
 */
export const selectionReducer = (state = {}, action) => {
  switch (action.type) {
    case ActionTypes.SELECT_INITIATIVE:
      return {
        ...state,
        selectedInitiative: action.payload
      };

    case ActionTypes.CLEAR_SELECTED_INITIATIVE:
      return {
        ...state,
        selectedInitiative: null
      };

    // Add more selection types as needed
    default:
      return state;
  }
};

/**
 * Root Reducer
 * Combines all reducers into a single state tree
 */
export const rootReducer = (state = {}, action) => {
  // Handle special batch actions
  if (action.type === 'BATCH_ACTIONS' && Array.isArray(action.payload)) {
    return action.payload.reduce((currentState, batchAction) => {
      return rootReducer(currentState, batchAction);
    }, state);
  }

  // Handle direct state updates (backward compatibility)
  if (action.type === 'DIRECT_STATE_UPDATE') {
    return {
      ...state,
      ...action.payload
    };
  }

  // Apply all domain reducers
  return {
    ui: uiReducer(state.ui, action),
    initiatives: initiativesReducer(state.initiatives, action),
    chat: chatReducer(state.chat, action),
    user: userReducer(state.user, action),
    strategies: strategiesReducer(state.strategies, action),
    portfolio: portfolioReducer(state.portfolio, action),
    system: systemReducer(state.system, action),
    selection: selectionReducer(state.selection, action)
  };
};

/**
 * Reducer Utilities
 */

/**
 * Get the reducer for a specific domain
 */
export function getReducerForDomain(domain) {
  const reducers = {
    ui: uiReducer,
    initiatives: initiativesReducer,
    chat: chatReducer,
    user: userReducer,
    strategies: strategiesReducer,
    portfolio: portfolioReducer,
    system: systemReducer,
    selection: selectionReducer
  };

  return reducers[domain] || ((state) => state);
}

/**
 * Check if a reducer should handle an action
 */
export function shouldReducerHandle(domain, action) {
  // Map action types to domains
  const actionDomainMap = {
    // UI actions
    SET_CURRENT_TAB: 'ui',
    SET_LOADING: 'ui',
    SHOW_MODAL: 'ui',
    HIDE_MODAL: 'ui',
    TOGGLE_SIDEBAR: 'ui',
    SET_THEME: 'ui',
    SHOW_TOAST: 'ui',
    HIDE_TOAST: 'ui',

    // Initiative actions
    ADD_INITIATIVE: 'initiatives',
    UPDATE_INITIATIVE: 'initiatives',
    DELETE_INITIATIVE: 'initiatives',
    SET_INITIATIVES: 'initiatives',

    // Chat actions
    ADD_CHAT_MESSAGE: 'chat',
    CLEAR_CHAT: 'chat',
    SET_TYPING: 'chat',
    UPDATE_CONVERSATION_CONTEXT: 'chat',
    SET_CONVERSATION_DEPTH: 'chat',

    // User actions
    SET_API_KEY: 'user',
    UPDATE_SETTINGS: 'user',
    UPDATE_PREFERENCES: 'user',
    SET_MODEL: 'user',

    // Strategy actions
    SET_UPLOADED_STRATEGY: 'strategies',
    SET_EMERGENT_STRATEGY: 'strategies',
    SET_CURRENT_STRATEGY: 'strategies',
    CLEAR_STRATEGIES: 'strategies',

    // Portfolio actions
    SET_PORTFOLIO_ANALYSIS: 'portfolio',
    SET_PORTFOLIO_CONFLICTS: 'portfolio',
    UPDATE_PORTFOLIO_STATUS: 'portfolio',

    // System actions
    APP_INITIALIZED: 'system',
    APP_ERROR: 'system',
    APP_RESET: 'system',
    ADD_ERROR: 'system',
    CLEAR_ERRORS: 'system',
    SET_VERSION: 'system',
    SET_INITIALIZED: 'system',

    // Selection actions
    SELECT_INITIATIVE: 'selection',
    CLEAR_SELECTED_INITIATIVE: 'selection'
  };

  // Some actions affect multiple domains
  const crossDomainActions = [
    'APP_RESET',
    'DIRECT_STATE_UPDATE',
    'BATCH_ACTIONS'
  ];

  const actionType = action.type.replace('@@', ''); // Remove Redux prefixes
  
  return crossDomainActions.includes(actionType) || 
         actionDomainMap[actionType] === domain;
}

/**
 * Create a scoped reducer that only handles specific action types
 */
export function createScopedReducer(reducer, actionTypes) {
  return (state, action) => {
    if (actionTypes.includes(action.type)) {
      return reducer(state, action);
    }
    return state;
  };
}

/**
 * Combine multiple reducers for the same domain
 */
export function combineReducers(reducers) {
  return (state = {}, action) => {
    const nextState = {};
    let hasChanged = false;

    for (const key in reducers) {
      const reducer = reducers[key];
      const previousStateForKey = state[key];
      const nextStateForKey = reducer(previousStateForKey, action);

      nextState[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }

    return hasChanged ? nextState : state;
  };
}

// Export for debugging in development
if (import.meta.env?.DEV) {
  window.StoreReducers = {
    rootReducer,
    uiReducer,
    initiativesReducer,
    chatReducer,
    userReducer,
    strategiesReducer,
    portfolioReducer,
    systemReducer,
    selectionReducer,
    getReducerForDomain,
    shouldReducerHandle
  };
  console.log('🔧 Store reducers available at window.StoreReducers');
}
